import { useEffect, useState } from "react";
import { DialogContent, Grid, TextField, Checkbox, FormControlLabel } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "store/slice/users/userSlice";
import { UpdateUserConnectAccountsAPI } from "api/userApi";
import MDDialog from "components/MDDialog";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const AddUsers = ({ dialogOpen, onClose, account }) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.users || []);
  const usersLoading = useSelector((state) => state.users?.loading);
  const [emails, setEmails] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dialogOpen) {
      dispatch(getUsers());
      setSelectedUsers([]);
    }
  }, [dialogOpen, dispatch]);

  useEffect(() => {
    if (!dialogOpen || !account || users.length === 0) {
      return;
    }

    const accountId = account._id || account.connectAccountId;
    if (!accountId) {
      return;
    }

    const preselectedUsers = users
      .filter((user) =>
        Array.isArray(user.connectAccounts) &&
        user.connectAccounts.some(
          (connectAccount) => String(connectAccount) === String(accountId)
        )
      )
      .map((user) => user._id)
      .filter(Boolean);

    setSelectedUsers(preselectedUsers);
  }, [dialogOpen, account, users]);

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (!account?._id) {
      return;
    }

    console.log("Saving selected users for account:", account._id, selectedUsers);
    setSaving(true);

    // Prepare updates for all users
    const updates = users.map((user) => {
      const userId = user._id;
      let connectAccounts = Array.isArray(user.connectAccounts) ? [...user.connectAccounts] : [];

      if (selectedUsers.includes(userId)) {
        // Push: add account._id if not already present
        if (!connectAccounts.includes(account._id)) {
          connectAccounts.push(account._id);
        }
      } else {
        // Pull: remove account._id if present
        connectAccounts = connectAccounts.filter((id) => id !== account._id);
      }

      return {
        userId,
        connectAccounts,
      };
    });

    const payload = {
      data: updates,
    };

    try {
      await UpdateUserConnectAccountsAPI(payload);
      onClose();
    } catch (error) {
      console.error("Error updating users connectAccounts:", error);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDDialog
        // dialogTitle="Add users"
      dialogTitle={account ? `Add users for ${account.title || account.connectAccountId}` : "Add users"}
      open={dialogOpen}
      dialogClose={onClose}
      closeIcon
      minWidth="320px"
      maxWidth="450px"
    >
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox mb={2}>
              {usersLoading ? (
                <MDTypography variant="body2" color="text">
                  Loading users...
                </MDTypography>
              ) : users.length > 0 ? (
                users.map((user) => {
                  const userId = user._id || user.email;
                  return (
                    <MDBox key={userId} mb={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={selectedUsers.includes(userId)}
                            onChange={() => handleUserToggle(userId)}
                          />
                        }
                        label={
                          <MDBox>
                            <MDTypography variant="body2" fontWeight="medium">
                              {`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              {user.email}
                            </MDTypography>
                          </MDBox>
                        }
                      />
                    </MDBox>
                  );
                })
              ) : (
                <MDTypography variant="body2" color="text">
                  No users found.
                </MDTypography>
              )}
            </MDBox>
          </Grid>

          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
                Cancel
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                onClick={handleSave}
                disabled={saving || !account?._id}
              >
                {saving ? "Saving..." : "Save"}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </DialogContent>
    </MDDialog>
  );
};

export default AddUsers;
